import React from "react";
import ReactDOM from "react-dom";
import { pdf } from "@react-pdf/renderer";
import { getAzureBlobFile } from "../../../../services/container";
import { mergePdfs } from "../../../../components/download/pdfUtils";
import { captureCharacterizationPdf } from "./capturePdf";
import { loadCharacterizationComments } from "./characterizationComments";
import { toast } from "react-toastify";
import {
  getContainerCharacterization,
  getContainerClientRegisteredInfo,
} from "../../../../services/container";
import { getSettingsData } from "../../../../services/settings";
import { formatOutletData, aggregateMaterialDelivery } from "../utils/utils";
import { repDescriptions } from "../../../../shared/utils/utils";
import CharacterizationReports from "../../characterizationDetails/CharacterizationReports";
import { ContainerViewMaterialForPDF } from "../../characterizationDetails/ContainerViewMaterialForPDF";
import {
  getEffectiveThresholdCm,
  getRegulationTypeFromContainer,
} from "../../characterizationDetails/constants/regulation";
import { mergeMaterialFluxes } from "./mergeMaterialFluxes";
import { sortObjectsInArrayByKeyName } from "../../../../components/utils/Object";

const fetchCharacterizationData = async (
  period,
  container,
  superContainer,
  i18n,
  t,
  characterizationType = "1199",
  sizeThreshold = 40,
) => {
  const containerId = superContainer ? superContainer._id : container?._id;
  if (!period || !containerId) {
    return null;
  }

  try {
    const requests = getContainerCharacterization(
      {
        periodMaterialStart: period.periodMaterialStartSubfilter,
        periodMaterialStop: period.periodMaterialStopSubfilter,
        customDate: !!period.periodMaterialStopSubfilter,
        language: i18n.language,
        characterizationType,
        sizeThreshold: getEffectiveThresholdCm(
          characterizationType,
          sizeThreshold,
        ),
      },
      containerId,
    );
    const requestArray = Array.isArray(requests) ? requests : [requests];
    const responses = await Promise.all(requestArray);
    const validResponses = responses.filter((r) => r?.status === 200);
    if (validResponses.length === 0) {
      return null;
    }
    const responseData = validResponses[0].data;

    const sortedHistory = (responseData.historyMaterial || []).sort(
      (a, b) =>
        new Date(b._time || 0).getTime() - new Date(a._time || 0).getTime(),
    );

    // Merge materials description
    const mergeConfig = JSON.parse(
      localStorage.getItem("materialsDescription") || "[]",
    ).map((v) => ({
      referenceName: v.referenceName,
      possibleNames: v.possibleNames,
    }));

    // Merge materialDelivery by cls and sizeRange
    const concatenatedMaterials = [
      ...(responseData.materialDelivery || [])
        .reduce((r, o) => {
          const sizeRange = o.sizeRange || "total";
          const key = `${o.cls}_${sizeRange}`;
          const item =
            r.get(key) ||
            Object.assign({}, o, {
              sizeRange,
              count: 0,
              massObject: 0,
              volumeObject: 0,
              finesMass: 0,
              finesVolume: 0,
              surface: 0,
              materialTotal: 0,
              totalMassRecoverable: 0,
              totalVolumeRecoverable: 0,
              recoverable: false,
            });
          item.count += +(o.count || 0);
          item.massObject += +(o.massObject || 0);
          item.volumeObject += +(o.volumeObject || 0);
          item.finesMass += +(o.finesMass || 0);
          item.finesVolume += +(o.finesVolume || 0);
          item.surface += +(o.surface || 0);
          item.materialTotal += +(o.materialTotal || 0);
          item.totalMassRecoverable += +(o.totalMassRecoverable || 0);
          item.totalVolumeRecoverable += +(o.totalVolumeRecoverable || 0);
          item.recoverable =
            o.recoverable !== undefined ? o.recoverable : item.recoverable;
          item.recoverableWithOutlet =
            o.data?.outletData?.recoverableWithOutlet;
          item.recoverableWithNoOutlet =
            o.data?.outletData?.recoverableWithNoOutlet;
          return r.set(key, item);
        }, new Map())
        .values(),
    ];

    const mergedFluxes = mergeMaterialFluxes(
      concatenatedMaterials,
      mergeConfig,
    );
    const sortedFluxes = sortObjectsInArrayByKeyName(mergedFluxes, "cls");
    const displayedMaterialDelivery = aggregateMaterialDelivery(sortedFluxes);

    const formattedOutletData = formatOutletData(
      responseData?.outletData,
      t,
      i18n.language,
    );

    let formattedRepData = {};
    if (characterizationType === "EPR" && responseData?.repData) {
      const newRepData = Array.isArray(responseData.repData)
        ? responseData.repData
        : [responseData.repData].filter(Boolean);
      if (newRepData.length > 0) {
        const mergedRep = newRepData.reduce(
          (acc, item) => {
            if (!item || !item.data) return acc;
            acc.volumeTotal += item.volumeTotal || 0;
            acc.massTotal += item.massTotal || 0;
            Object.entries(item.data).forEach(([rep, values]) => {
              const repKey = repDescriptions[rep]?.category || rep;
              if (!acc.data[repKey]) {
                acc.data[repKey] = {
                  massObject: 0,
                  volumeObject: 0,
                  objects: [],
                };
              }
              acc.data[repKey].massObject += values.massObject || 0;
              acc.data[repKey].volumeObject += values.volumeObject || 0;
              acc.data[repKey].objects = [
                ...new Set([
                  ...acc.data[repKey].objects,
                  ...(values.objects || []),
                ]),
              ];
            });
            return acc;
          },
          {
            volumeTotal: 0,
            massTotal: 0,
            data: {
              "PMCB Category 1": {
                massObject: 0,
                volumeObject: 0,
                objects: [],
              },
              "PMCB Category 2": {
                massObject: 0,
                volumeObject: 0,
                objects: [],
              },
              ABJ: {
                massObject: 0,
                volumeObject: 0,
                objects: [],
                description: t("Articles de bricolage et de jardin"),
              },
              ALS: {
                massObject: 0,
                volumeObject: 0,
                objects: [],
                description: t("Articles de loisirs et de sport"),
              },
              D3E: {
                massObject: 0,
                volumeObject: 0,
                objects: [],
                description: t(
                  "Responsabilité Élargie du Producteur pour les Déchets d'Équipements Électriques et Électroniques",
                ),
              },
            },
          },
        );

        const repChartLabels = Object.keys(mergedRep.data).sort();
        const repChartData = repChartLabels
          .filter((key) => mergedRep.data[key].volumeObject > 0)
          .map((key) => ({
            name: t(key),
            value: (
              (mergedRep.data[key].volumeObject / mergedRep.volumeTotal) *
              100
            ).toFixed(1),
          }));
        formattedRepData = {
          data: mergedRep.data,
          repChartData,
          repChartLabels: repChartLabels.map((key) => t(key)),
          volumeTotal: mergedRep.volumeTotal,
        };
      }
    }

    return {
      historyMaterial: sortedHistory,
      materialDelivery: displayedMaterialDelivery || [],
      parentCategoriesList: responseData.parentCategoriesList || [],
      recoverableMaterialList: responseData.recoverableMaterialList || [],
      totalWasteMass: responseData.totalWasteMass || 0,
      totalWasteVolume: responseData.totalWasteVolume || 0,
      totalMassRecoverable: responseData.totalMassRecoverable || 0,
      totalVolumeRecoverable: responseData.totalVolumeRecoverable || 0,
      materialTotal: responseData.materialTotal || 0,
      recoverableWithOutlet:
        responseData?.outletData?.recoverableWithOutlet?.massTotal || 0,
      recoverableWithNoOutlet:
        responseData?.outletData?.recoverableWithNoOutlet?.massTotal || 0,
      outletData: formattedOutletData,
      repData: formattedRepData,
      totalFinesMass: responseData.totalFinesMass || 0,
      totalFinesVolume: responseData.totalFinesVolume || 0,
      finesBelowThreshold: responseData.finesBelowThreshold || {
        count: 0,
        volumeObject: 0,
        massObject: 0,
      },
    };
  } catch (err) {
    console.error(
      "Erreur lors du chargement des données de caractérisation:",
      err,
    );
    return null;
  }
};

export const exportEmailFromRow = async ({
  period,
  container,
  superContainer,
  client,
  i18n,
  t,
  setPdfFile,
  setOpenModalExport,
  setInitialPdfModalAction,
  selectedFilesForExport = [],
}) => {
  try {
    const regulationType = getRegulationTypeFromContainer(container);

    // Get size threshold from settings
    let sizeThreshold = 40; // Default
    try {
      const settingsResponse = await getSettingsData();
      if (settingsResponse.data?.settings?.finesSizeThreshold !== undefined) {
        sizeThreshold = settingsResponse.data.settings.finesSizeThreshold;
      }
    } catch (error) {
      console.error("Error loading settings:", error);
    }

    // Fetch characterization data
    const data = await fetchCharacterizationData(
      period,
      container,
      superContainer,
      i18n,
      t,
      regulationType,
      sizeThreshold,
    );
    if (!data) {
      toast.error(t("No data available for export"));
      return;
    }

    // Create hidden container and render components
    const hiddenContainer = document.createElement("div");
    hiddenContainer.style.position = "fixed";
    hiddenContainer.style.left = "0";
    hiddenContainer.style.top = "0";
    hiddenContainer.style.width = "1200px";
    hiddenContainer.style.height = "8000px";
    hiddenContainer.style.overflow = "auto";
    hiddenContainer.style.zIndex = "-9999";
    hiddenContainer.style.opacity = "0";
    hiddenContainer.style.pointerEvents = "none";
    document.body.appendChild(hiddenContainer);

    const pdfMassChartRef = { current: null };
    const pdfMassTableRef = { current: null };
    const pdfOutletChartRef = { current: null };
    const pdfOutletTableRef = { current: null };
    const pdfVolumeChartRef = { current: null };
    const pdfObjectsChartRef = { current: null };
    const pdfRepChartRef = { current: null };
    const pdfRepTableRef = { current: null };

    ReactDOM.render(
      <div
        style={{
          width: "1200px",
          minHeight: "8000px",
          padding: "20px",
          backgroundColor: "white",
        }}
      >
        <CharacterizationReports
          totalWeight={period?.weight ? period.weight : 0}
          materialDelivery={data.materialDelivery}
          totalWasteMass={data.totalWasteMass}
          totalWasteVolume={data.totalWasteVolume}
          recoverableMaterialList={data.recoverableMaterialList}
          parentCategoriesList={data.parentCategoriesList}
          totalVolumeRecoverable={data.totalVolumeRecoverable}
          totalMassRecoverable={data.totalMassRecoverable}
          recoverableWithOutlet={data.recoverableWithOutlet}
          recoverableWithNoOutlet={data.recoverableWithNoOutlet}
          materialTotal={data.materialTotal}
          outletData={data.outletData}
          repData={data.repData}
          characterizationType={regulationType}
          sizeThreshold={sizeThreshold}
          totalFinesMass={data.totalFinesMass}
          totalFinesVolume={data.totalFinesVolume}
          finesBelowThreshold={data.finesBelowThreshold}
          refs={{
            massChartRef: pdfMassChartRef,
            massTableRef: pdfMassTableRef,
            outletChartRef: pdfOutletChartRef,
            outletTableRef: pdfOutletTableRef,
            volumeChartRef: pdfVolumeChartRef,
            objectsChartRef: pdfObjectsChartRef,
            repChartRef: pdfRepChartRef,
            repTableRef: pdfRepTableRef,
          }}
        />
        <ContainerViewMaterialForPDF
          refs={{
            massChartRef: pdfMassChartRef,
            massTableRef: pdfMassTableRef,
            outletChartRef: pdfOutletChartRef,
            outletTableRef: pdfOutletTableRef,
            volumeChartRef: pdfVolumeChartRef,
            objectsChartRef: pdfObjectsChartRef,
            repChartRef: pdfRepChartRef,
            repTableRef: pdfRepTableRef,
          }}
          parentCategoriesList={data.parentCategoriesList}
          totalWasteMass={data.totalWasteMass}
          totalWasteVolume={data.totalWasteVolume}
          materialDelivery={data.materialDelivery}
          recoverableMaterialList={data.recoverableMaterialList}
          totalVolumeRecoverable={data.totalVolumeRecoverable}
          totalMassRecoverable={data.totalMassRecoverable}
          recoverableWithOutlet={data.recoverableWithOutlet}
          recoverableWithNoOutlet={data.recoverableWithNoOutlet}
          materialTotal={data.materialTotal}
          outletData={data.outletData}
          totalWeight={period?.weight ? period.weight : 0}
          repData={data.repData}
          characterizationType={regulationType}
          sizeThreshold={sizeThreshold}
          totalFinesMass={data.totalFinesMass}
          totalFinesVolume={data.totalFinesVolume}
          finesBelowThreshold={data.finesBelowThreshold}
        />
      </div>,
      hiddenContainer,
    );

    // Wait for components to render
    const waitForRefs = () => {
      return new Promise((resolve) => {
        let attempts = 0;
        const maxAttempts = 100;
        const checkRefs = () => {
          attempts++;
          const refsStatus = {
            massChart: !!pdfMassChartRef.current,
            massTable: !!pdfMassTableRef.current,
            volumeChart: !!pdfVolumeChartRef.current,
            objectsChart: !!pdfObjectsChartRef.current,
          };
          const allRefsAttached = Object.values(refsStatus).some(Boolean);
          if (allRefsAttached || attempts >= maxAttempts) {
            setTimeout(resolve, 4000);
          } else {
            setTimeout(checkRefs, 100);
          }
        };
        checkRefs();
      });
    };
    await waitForRefs();

    let registrationClientCode;
    let filesToMergeFromPeriod = [];
    let pdfClient = client;
    try {
      const containerId = superContainer?._id ?? container?._id;
      const periodStart = period?.start != null ? new Date(period.start) : null;
      if (containerId && periodStart) {
        const clientResponse = await getContainerClientRegisteredInfo(
          {
            start: new Date(periodStart.getTime() - 1).toISOString(),
            stop: new Date(periodStart.getTime() + 1).toISOString(),
          },
          containerId,
        );
        if (clientResponse?.data?.client) {
          const clientData =
            typeof clientResponse.data.client === "string"
              ? JSON.parse(clientResponse.data.client)
              : clientResponse.data.client;
          registrationClientCode =
            clientData?.code != null ? String(clientData.code) : "";
          pdfClient = {
            ...(client || {}),
            ...clientData,
            name: clientData?.name || client?.name,
            address: clientData?.address || client?.address,
            clientUniqueId:
              clientData?.clientUniqueId || client?.clientUniqueId || "",
          };
          if (Array.isArray(clientData?.files)) {
            filesToMergeFromPeriod = clientData.files;
          }
        } else if (
          clientResponse?.data &&
          Object.keys(clientResponse.data).length > 0
        ) {
          registrationClientCode = "";
        }
      }
    } catch (err) {
      console.warn("Could not load client registration for PDF:", err);
    }

    // Generate PDF
    const comments = await loadCharacterizationComments({
      container,
      superContainer,
      historyMaterial: data.historyMaterial,
    });
    const { pdfContent, pdfFilename } = await captureCharacterizationPdf({
      refs: {
        massChartRef: pdfMassChartRef,
        massTableRef: pdfMassTableRef,
        outletChartRef: pdfOutletChartRef,
        outletTableRef: pdfOutletTableRef,
        volumeChartRef: pdfVolumeChartRef,
        objectsChartRef: pdfObjectsChartRef,
        repChartRef: pdfRepChartRef,
        repTableRef: pdfRepTableRef,
      },
      period,
      superContainer,
      container,
      historyMaterial: data.historyMaterial,
      totalWasteMass: data.totalWasteMass,
      totalMassRecoverable: data.totalMassRecoverable,
      totalWasteVolume: data.totalWasteVolume,
      totalVolumeRecoverable: data.totalVolumeRecoverable,
      totalWeight: period?.weight ? period.weight : 0,
      client: pdfClient,
      registrationClientCode,
      wasteCategoryName: period?.waste_category || "-",
      parentCategoriesList: data.parentCategoriesList,
      outletData: data.outletData,
      comments,
      t,
      repData: data.repData,
      characterizationType: regulationType,
      sizeThreshold,
    });

    // Cleanup
    ReactDOM.unmountComponentAtNode(hiddenContainer);
    document.body.removeChild(hiddenContainer);

    // Generate PDF blob and append documents joints (like characterisation v1)
    const mainPdfBlob = await pdf(pdfContent).toBlob();
    const pdfList = [{ filename: pdfFilename, pdfBlob: mainPdfBlob }];

    let filesToMerge = selectedFilesForExport || [];
    if (filesToMerge.length === 0 && filesToMergeFromPeriod.length > 0) {
      filesToMerge = filesToMergeFromPeriod;
    }

    if (filesToMerge.length > 0) {
      const azureUrl = process.env.REACT_APP_AZURE_URL || "";
      const azureFiles = await Promise.all(
        filesToMerge.map(async (filePath) => {
          const { data: fileBlob } = await getAzureBlobFile({
            blobUrl: `${azureUrl}${filePath}`,
          });
          const fileName = filePath.split("/").pop();
          return { filename: fileName, pdfBlob: fileBlob };
        }),
      );
      pdfList.push(...azureFiles);
      const mergedPdfBlob = await mergePdfs(pdfList);
      setPdfFile([{ filename: pdfFilename, pdfBlob: mergedPdfBlob }]);
    } else {
      setPdfFile(pdfList);
    }
    setInitialPdfModalAction("share");
    setOpenModalExport(true);
  } catch (error) {
    console.error("Error generating PDF for email:", error);
    toast.error(t("Error generating PDF"));
  }
};
